import ConservationState from "./enums/ConservationState";
import DocumentState from "./enums/DocumentState";
import Annotation from "./Annotation";
import { UserSummaryOutDTO } from "./User";
import InspectionModeItem from "./InspectionModeItem";
import CHUsageItem from "./CHUsageItem";
import {
  InspectionModeInDTO,
  InspectionModeOutDTO,
} from "./InspectionModeBase";
import { CHUsageInDTO, CHUsageOutDTO } from "./CHUsageBase";
import AnnexData from "./AnnexData";

/**
 * Base model - mirrors ConditionReportSchema on backend
 */
class ConditionReportBase {
  constructor(
    public name: string,
    public description: string,
    public id_annotations?: number[] | undefined,
    public id_province?: number,
    public id_municipality?: number,
    public address?: string,
    public date?: string,
    public author?: string,
    public cultural_sphere?: string,
    public legal_status?: string,
    public conservation_history?: string,
    public reference_cartography_municipality?: string,
    public reference_cartography_cadastral_sheet?: string,
    public reference_cartography_cadastral_parcel?: string,
    public reference_cartography_other?: string,
    public parent_asset_name?: string,
    public specific_location?: string,
    public colocation_characteristics?: string,
    public techniques?: string,
    public object?: string,
    public subject?: string,
    public diagnosis_description?: string,
    public condition_recommendations?: string,
    public state_of_conservation?: ConservationState,
    public environmental_conditions_temperature?: string,
    public environmental_conditions_relative_humidity?: string,
    public environmental_conditions_current_weather_condition?: string,
    public environmental_conditions_past_week_weather_condition?: string,
    public survey_date?: string,
    public report_references?: string,
    public status?: DocumentState,
    public survey_responsible?: string,
  ) {}

  public static EmptyInstance(): ConditionReportBase {
    return new ConditionReportBase(
      "", // name
      "", // description
      [], // id_annotations
      undefined, // id_province
      undefined, // id_municipality
      "", // address
      "", // date
      "", // author
      "", // cultural_sphere
      "", // legal_status
      "", // conservation_history
      "", // reference_cartography_municipality
      "", // reference_cartography_cadastral_sheet
      "", // reference_cartography_cadastral_parcel
      "", // reference_cartography_other
      "", // parent_asset_name
      "", // specific_location
      "", // colocation_characteristics
      "", // techniques
      "", // object
      "", // subject
      "", // diagnosis_description
      "", // condition_recommendations
      undefined, // state_of_conservation
      "", // environmental_conditions_temperature
      "", // environmental_conditions_relative_humidity
      "", // environmental_conditions_current_weather_condition
      "", // environmental_conditions_past_week_weather_condition
      "", // survey_date
      "", // report_references
      undefined, // status
      "", // survey_responsible
    );
  }

  public static GetProgress(report: ConditionReportBase): number {
    let total = 0;
    let populated = 0;
    let key: keyof ConditionReportBase;
    // go through all the properties
    for (key in report) {
      total++;
      if (report[key]) {
        if (!Array.isArray(report[key])) {
          populated++;
        } else if (Array(report[key]).length > 0) {
          populated++;
        }
      }
    }
    return Math.round((populated / total) * 100);
  }
}

/**
 * Input DTO - mirrors ConditionReportInDTO on backend
 */
class ConditionReportInDTO extends ConditionReportBase {
  constructor(
    name: string,
    description: string,
    public ch_usages?: CHUsageInDTO[],
    public ch_inspection_modes?: InspectionModeInDTO[],
    public id_annex_datas?: number[],
    public id_users?: number[],
    other?: Partial<ConditionReportBase>,
  ) {
    super(
      name,
      description,
      other?.id_annotations,
      other?.id_province,
      other?.id_municipality,
      other?.address,
      other?.date,
      other?.author,
      other?.cultural_sphere,
      other?.legal_status,
      other?.conservation_history,
      other?.reference_cartography_municipality,
      other?.reference_cartography_cadastral_sheet,
      other?.reference_cartography_cadastral_parcel,
      other?.reference_cartography_other,
      other?.parent_asset_name,
      other?.specific_location,
      other?.colocation_characteristics,
      other?.techniques,
      other?.object,
      other?.subject,
      other?.diagnosis_description,
      other?.condition_recommendations,
      other?.state_of_conservation,
      other?.environmental_conditions_temperature,
      other?.environmental_conditions_relative_humidity,
      other?.environmental_conditions_current_weather_condition,
      other?.environmental_conditions_past_week_weather_condition,
      other?.survey_date,
      other?.report_references,
      other?.status,
      other?.survey_responsible,
    );
  }

  public static FromOutDTO(
    outDto: ConditionReportOutDTO,
  ): ConditionReportInDTO {
    const crdto = new ConditionReportInDTO(
      outDto.name,
      outDto.description,
      outDto.use.map((u) => ({
        ch_usage_id: u.ch_usage.id!,
        description: u.description,
      })) as CHUsageInDTO[],
      outDto.inspection_modes.map((m) => ({
        inspection_mode_id: m.inspection_mode.id!,
        description: m.description,
      })) as InspectionModeInDTO[],
      outDto.id_annex_datas,
      outDto.users.map((u) => u.id),
      outDto,
    );

    crdto.id_annotations = outDto.annotations?.map((a) => a.id!) ?? [];
    return crdto;
  }
}

/**
 * Output DTO - mirrors ConditionReportOutDTO on backend
 */
class ConditionReportOutDTO extends ConditionReportBase {
  constructor(
    name: string,
    description: string,
    public id: number,
    public id_user: number,
    public id_e3dmodel: number,
    public GUID?: string,
    public created_on?: Date,
    public use: CHUsageOutDTO[] = [],
    public inspection_modes: InspectionModeOutDTO[] = [],
    public annotations: Annotation[] = [],
    public annex_datas: AnnexData[] = [],
    public users: UserSummaryOutDTO[] = [],
    public id_annex_datas?: number[],
    other?: Partial<ConditionReportBase>,
  ) {
    super(
      name,
      description,
      other?.id_annotations,
      other?.id_province,
      other?.id_municipality,
      other?.address,
      other?.date,
      other?.author,
      other?.cultural_sphere,
      other?.legal_status,
      other?.conservation_history,
      other?.reference_cartography_municipality,
      other?.reference_cartography_cadastral_sheet,
      other?.reference_cartography_cadastral_parcel,
      other?.reference_cartography_other,
      other?.parent_asset_name,
      other?.specific_location,
      other?.colocation_characteristics,
      other?.techniques,
      other?.object,
      other?.subject,
      other?.diagnosis_description,
      other?.condition_recommendations,
      other?.state_of_conservation,
      other?.environmental_conditions_temperature,
      other?.environmental_conditions_relative_humidity,
      other?.environmental_conditions_current_weather_condition,
      other?.environmental_conditions_past_week_weather_condition,
      other?.survey_date,
      other?.report_references,
      other?.status,
      other?.survey_responsible,
    );
  }

  public static EmptyInstance(): ConditionReportOutDTO {
    return new ConditionReportOutDTO(
      "",
      "",
      0,
      0,
      0,
      "",
      new Date(),
      [],
      [],
      [],
      [],
      [],
      [],
      ConditionReportBase.EmptyInstance(),
    );
  }
}

/**
 * Update DTO - mirrors ConditionReportUpdateDTO on backend
 */
class ConditionReportUpdateDTO {
  constructor(
    public name?: string,
    public description?: string,
    public id_annotations?: number[],
    public ch_usages?: CHUsageItem[],
    public ch_inspection_modes?: InspectionModeItem[],
    public annex_datas?: AnnexData[],
    public id_province?: number,
    public id_municipality?: number,
    public address?: string,
    public date?: string,
    public author?: string,
    public cultural_sphere?: string,
    public legal_status?: string,
    public conservation_history?: string,
    public reference_cartography_municipality?: string,
    public reference_cartography_cadastral_sheet?: string,
    public reference_cartography_cadastral_parcel?: string,
    public reference_cartography_other?: string,
    public parent_asset_name?: string,
    public specific_location?: string,
    public colocation_characteristics?: string,
    public techniques?: string,
    public object?: string,
    public subject?: string,
    public diagnosis_description?: string,
    public state_of_conservation?: ConservationState,
    public environmental_conditions_temperature?: string,
    public environmental_conditions_relative_humidity?: string,
    public environmental_conditions_current_weather_condition?: string,
    public environmental_conditions_past_week_weather_condition?: string,
    public survey_date?: string,
    public report_references?: string,
    public status?: DocumentState,
    public survey_responsible?: string,
    public id_users?: number[],
  ) {}
}

export {
  ConditionReportBase,
  ConditionReportInDTO,
  ConditionReportOutDTO,
  //   ConditionReportUpdateDTO,
};
